/**
 * @file src/utils/maintenance.ts
 * @fileoverview Provides utility functions for managing the application's maintenance mode.
 * It allows checking if maintenance mode is active (based on environment variables or localStorage)
 * and setting the maintenance mode status in localStorage.
 */

/**
 * Checks if the application is currently in maintenance mode.
 * It first checks localStorage for a 'maintenanceMode' setting,
 * then falls back to the `VITE_MAINTENANCE_MODE` environment variable.
 *
 * @returns {boolean} True if maintenance mode is active, false otherwise.
 */
export const getMaintenanceMode = (): boolean => {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem('maintenanceMode');
    if (stored !== null) return stored === 'true';
  }
  // Default to true if VITE_IS_MAINTENANCE is not set
  return import.meta.env.VITE_IS_MAINTENANCE === undefined ? true : import.meta.env.VITE_IS_MAINTENANCE === 'true';
};

/**
 * Sets the maintenance mode status in localStorage.
 * This allows overriding the environment variable setting for the current browser session.
 *
 * @param {boolean} value - True to enable maintenance mode, false to disable.
 */
export const setMaintenanceMode = (value: boolean): void => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('maintenanceMode', value ? 'true' : 'false');
  }
};
